import {
  Component,
  computed,
  inject,
  model,
  signal,
  viewChild,
  ViewEncapsulation,
} from "@angular/core";
import { Clipboard } from "@angular/cdk/clipboard";
import { rxResource } from "@angular/core/rxjs-interop";
import { RouterLink } from "@angular/router";
import { HttpErrorResponse } from "@angular/common/http";
import { catchError, firstValueFrom, throwError } from "rxjs";

import { NzSpaceModule } from "ng-zorro-antd/space";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzDividerModule } from "ng-zorro-antd/divider";
import { NzEmptyModule } from "ng-zorro-antd/empty";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzPopconfirmModule } from "ng-zorro-antd/popconfirm";
import {
  NzTableFilterFn,
  NzTableFilterList,
  NzTableModule,
  NzTableSortFn,
} from "ng-zorro-antd/table";
import { NzTagModule } from "ng-zorro-antd/tag";

import { ProjectService } from "../../shared/services/project.service";
import { httpErrorMessage } from "../../shared/http-error-message";
import { S3BucketInterface } from "../../models/s3-bucket.model";
import { BucketFormComponent } from "./bucket-form/bucket-form.component";
import { NzRadioModule } from "ng-zorro-antd/radio";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NzAlertComponent } from "ng-zorro-antd/alert";
import { NzSpinComponent } from "ng-zorro-antd/spin";
import { BucketLifecycleJsonEditorComponent } from "./bucket-lifecycle-json-editor/bucket-lifecycle-json-editor.component";
import { NzCollapseModule } from "ng-zorro-antd/collapse";
import { NzMessageService } from "ng-zorro-antd/message";
import { LifecycleRule } from "../../models/lifecycle-rule.types";
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzDrawerModule } from "ng-zorro-antd/drawer";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzInputModule } from "ng-zorro-antd/input";

/** Stable identity so the table's nzData does not change on every change detection pass. */
const NO_BUCKETS: readonly S3BucketInterface[] = [];

interface ColumnItem {
  name: string;
  key: string;
  width?: string;
  right?: boolean; // pin to the right (your Action col)
  sortFn?: NzTableSortFn<S3BucketInterface> | null;
  filters?: NzTableFilterList;
  filterFn?: NzTableFilterFn<S3BucketInterface> | null;
  filterMultiple?: boolean;
  searchable?: boolean;
  visible?: boolean;
  searchValue?: string;
}

@Component({
  selector: "app-s3-buckets",
  imports: [
    RouterLink,
    NzButtonModule,
    NzDividerModule,
    NzEmptyModule,
    NzIconModule,
    NzPopconfirmModule,
    NzTableModule,
    NzTagModule,
    NzRadioModule,
    NzSpaceModule,
    BucketFormComponent,
    FormsModule,
    CommonModule,
    NzAlertComponent,
    NzSpinComponent,
    BucketLifecycleJsonEditorComponent,
    NzCollapseModule,
    NzModalModule,
    NzDrawerModule,
    NzDropDownModule,
    NzInputModule,
    NzFormModule,
  ],
  templateUrl: "./s3-buckets.component.html",
  styleUrl: "./s3-buckets.component.less",
})
export class S3BucketsComponent {
  protected bucketForm = viewChild<BucketFormComponent>("addBucketForm");
  private readonly clipboard = inject(Clipboard);
  private projectService = inject(ProjectService);
  private notification = inject(NzNotificationService);
  private readonly message = inject(NzMessageService);

  private searchFilters = signal<Record<string, string>>({});
  protected isDeleting = signal(false);
  protected isFormOpen = signal(false);
  protected isSaving = signal<boolean>(false);
  protected selectedBucket = model<S3BucketInterface | null>(null);
  protected edittingBucket = model<S3BucketInterface | null>(null);

  protected buckets = computed(() => {
    if (this.bucketsResource.hasValue()) {
      const buckets = this.bucketsResource.value();
      const entries = Object.entries(this.searchFilters()) as [
        keyof S3BucketInterface,
        string,
      ][];

      return buckets.filter((bucket) =>
        entries.every(([key, value]) =>
          String(bucket[key] ?? "")
            .toLowerCase()
            .includes(value.toLowerCase()),
        ),
      );
    } else {
      return [];
    }
  });

  // rxResources
  protected bucketsResource = rxResource<S3BucketInterface[], never>({
    stream: () =>
      this.projectService.getS3Buckets().pipe(
        catchError((e: HttpErrorResponse) => {
          const err = new Error(e.error?.detail ?? e.message);
          (err as any).status = e.status;
          (err as any).code = e.error?.code;
          return throwError(() => err); // now a real Error → no wrapping, no warning
        }),
      ),
  });
  protected bucketLifecycleResource = rxResource<
    { bucket: S3BucketInterface; lifecycle_configuration: any[] },
    string | undefined
  >({
    params: () => this.selectedBucket()?.id ?? undefined,
    stream: ({ params }) => {
      // params is just the ID
      return this.projectService.getS3BucketLifecycleConfiguration(params).pipe(
        catchError((e: HttpErrorResponse) => {
          const err = new Error(e.error?.detail ?? e.message);
          (err as any).status = e.status;
          (err as any).code = e.error?.code;
          return throwError(() => err); // now a real Error → no wrapping, no warning
        }),
      );
    },
  });

  protected deleteBucket(bucket: S3BucketInterface) {
    if (this.isDeleting()) return;

    this.isDeleting.set(true);
    firstValueFrom(this.projectService.deregisterS3Bucket(bucket.id))
      .then(() => {
        this.notification.success("", `Bucket: ${bucket.name} deregistered`);
        this.bucketsResource.reload();
      })
      .catch((err) => {
        this.notification.error(
          "Error",
          httpErrorMessage(err as HttpErrorResponse),
        );
      })
      .finally(() => this.isDeleting.set(false));
  }
  protected editBucket(bucket: S3BucketInterface) {
    this.isFormOpen.set(true);
    this.edittingBucket.set(bucket);
  }
  protected saveLifecycleConfiguration(
    lifecycleConfiguration: LifecycleRule[],
  ) {
    // validation
    for (const config of lifecycleConfiguration) {
      if (config.Filter && config.Filter.Prefix) {
        if (
          config.Filter.Prefix &&
          config.Filter.Prefix.includes("REPLACE_ME/")
        ) {
          this.message.warning(
            "Please replace the placeholders for prefix (REPLACE_ME)",
          );
          return;
        }
      } else if (
        config.Filter &&
        config.Filter.And &&
        config.Filter.And.Prefix
      ) {
        if (
          config.Filter.And.Prefix &&
          config.Filter.And.Prefix.includes("REPLACE_ME/")
        ) {
          this.message.warning(
            "Please replace the placeholders for prefix (REPLACE_ME)",
          );
          return;
        }
      }
    }

    if (this.selectedBucket()) {
      this.isSaving.set(true);
      firstValueFrom(
        this.projectService.applyS3BucketLifecycleConfiguration(
          this.selectedBucket()!.id,
          lifecycleConfiguration,
        ),
      )
        .then((res) => {
          this.notification.success(
            "Success",
            `Lifecycle configuration for bucket: ${this.selectedBucket()?.name} update`,
          );
          this.bucketLifecycleResource.reload();
        })
        .catch((err: HttpErrorResponse) => {
          this.notification.error(
            "Error",
            httpErrorMessage(err as HttpErrorResponse),
          );
        })
        .finally(() => this.isSaving.set(false));
    }
  }
  protected syncBuckets() {
    const loading = this.message.loading("Syncing S3 Buckets", {
      nzDuration: 0,
    });
    firstValueFrom(this.projectService.syncS3Buckets())
      .then((res) => {
        this.notification.success("", "S3 buckets synced");
      })
      .catch((err: HttpErrorResponse) => {
        this.notification.error(
          "Error",
          httpErrorMessage(err as HttpErrorResponse),
        );
      })
      .finally(() => this.message.remove(loading.messageId));
  }

  // statics
  readonly columns: ColumnItem[] = [
    {
      name: "Name",
      key: "name",
      width: "26%",
      sortFn: (a, b) => {
        if (a.name == null && b.name == null) return 0;
        if (a.name == null) return 1;
        if (b.name == null) return -1;
        return a.name.localeCompare(b.name);
      },
      searchable: true,
      visible: false,
      searchValue: "",
    },
    {
      name: "Alias",
      key: "alias",
      width: "18%",
      sortFn: (a, b) => {
        if (a.alias == null && b.alias == null) return 0;
        if (a.alias == null) return 1;
        if (b.alias == null) return -1;
        return a.alias.localeCompare(b.alias);
      },
      searchable: true,
      visible: false,
      searchValue: "",
    },
    {
      name: "Region",
      key: "region",
      width: "16%",
      sortFn: (a, b) => {
        if (a.region == null && b.region == null) return 0;
        if (a.region == null) return 1;
        if (b.region == null) return -1;
        return a.region.localeCompare(b.region);
      },
      filters: [
        { text: "us-east-1", value: "us-east-1" },
        { text: "eu-west-1", value: "eu-west-1" },
        { text: "mx-central-1", value: "mx-central-1" },
      ],
      filterFn: (selected: string[], item) => selected.includes(item.region),
    },
    {
      name: "Description",
      key: "description",
      sortFn: (a, b) => {
        if (a.description == null && b.description == null) return 0;
        if (a.description == null) return 1;
        if (b.description == null) return -1;
        return a.description.localeCompare(b.description);
      },
    },
  ];

  readonly templates: any[] = [
    {
      key: "tag-to-deep-archive",
      name: "Send tagged objects to Deep Archive",
      hint: "Sends all objects, which have the given tag, to Deep Archive on the next lifecycle.",
      rules: {
        ID: "tag-to-deep-archive",
        Filter: {
          And: {
            Tags: [
              {
                Key: "to_deep_archive",
                Value: "true",
              },
            ],
            ObjectSizeGreaterThan: 5242880,
          },
        },
        Status: "Enabled",
        Transitions: [
          {
            Days: 0,
            StorageClass: "DEEP_ARCHIVE",
          },
        ],
      },
    },
    {
      key: "tag-to-glacier-ir",
      name: "Send tagged objects to Glacier Instant Retrieval",
      hint: "Sends all objects, which have the given tag, to Glacier Instant Retrieval on the next lifecycle.",
      rules: {
        ID: "tag-to-glacier-ir",
        Filter: {
          And: {
            Tags: [
              {
                Key: "to_glacier_ir",
                Value: "true",
              },
            ],
            ObjectSizeGreaterThan: 5242880,
          },
        },
        Status: "Enabled",
        Transitions: [
          {
            Days: 0,
            StorageClass: "GLACIER_IR",
          },
        ],
      },
    },
    {
      key: "all-under-prefix-to-glacier-ir",
      name: "Send all objects under a prefix to Glacier Instant Retrieval",
      hint: "Sends all objects, which are under the given prefix, to Glacier Instant Retrieval on the next lifecycle.",
      rules: {
        ID: "prefix-to-glacier-ir",
        Filter: {
          And: {
            Prefix: "REPLACE_ME/",
            ObjectSizeGreaterThan: 5242880,
          },
        },
        Status: "Enabled",
        Transitions: [
          {
            Days: 0,
            StorageClass: "GLACIER_IR",
          },
        ],
      },
    },
    {
      key: "all-under-prefix-to-deep-archive",
      name: "Send all objects under a prefix to Deep Archive",
      hint: "Sends all objects, which are under the given prefix, to Deep Archive on the next lifecycle.",
      rules: {
        ID: "prefix-to-deep-archive",
        Filter: {
          And: {
            Prefix: "REPLACE_ME/",
            ObjectSizeGreaterThan: 5242880,
          },
        },
        Status: "Enabled",
        Transitions: [
          {
            Days: 0,
            StorageClass: "DEEP_ARCHIVE",
          },
        ],
      },
    },
  ];
  protected copy(tpl: any): void {
    const ok = this.clipboard.copy(JSON.stringify(tpl.rules, null, 2));
    if (ok) {
      this.message.success(`Copied "${tpl.name}" to clipboard`);
    } else {
      this.message.error("Copy failed — select the JSON and copy manually");
    }
  }

  // helpers for ANTD table:
  protected search(col: ColumnItem): void {
    col.visible = false;
    col.searchValue;
    const newFilters = { ...this.searchFilters() };

    if (col.searchValue) newFilters[col.key] = col.searchValue;
    else {
      // col.searchValue is "" or null? delete key if exists
      if (col.key in newFilters) delete newFilters[col.key];
    }

    this.searchFilters.set(newFilters);
  }

  protected reset(col: ColumnItem): void {
    col.searchValue = "";
    col.visible = false;

    const newFilters = { ...this.searchFilters() };
    if (col.key in newFilters) delete newFilters[col.key];
    this.searchFilters.set(newFilters);
  }
}
